import { Helmet } from "react-helmet-async";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import WhatsAppButton from "@/components/WhatsAppButton";
import { useLanguage } from "@/contexts/LanguageContext";
import { useSiteContent } from "@/contexts/SiteContentContext";
import { absoluteUrl, buildHreflangAlternates, SITE_NAME } from "@/lib/seo";

const FALLBACK_CONTROLLER = {
  name: "WISETEC69 LLC.",
  address: "30 N Gould St Sten, 82801 Sheridan, Wyoming/USA",
  email: "info@digital-mallorca.de",
  phone: "+1 (716) 755-7656",
};

const CONTENT = {
  de: {
    title: "Datenschutzerklärung",
    subtitle: "DSGVO · LOPD-konform · Stand: 2026",
    sections: [
      {
        h: "1. Verantwortlicher",
        b: (c) => (
          <>
            Verantwortlich für die Verarbeitung personenbezogener Daten auf dieser Website ist:<br /><br />
            <strong>{c.name}</strong><br />
            {c.address}<br />
            E-Mail: <a className="text-sky-700 hover:underline" href={`mailto:${c.email}`}>{c.email}</a><br />
            Telefon: {c.phone}
          </>
        ),
      },
      {
        h: "2. Allgemeines zur Datenverarbeitung",
        b: () => (
          <>
            Wir verarbeiten personenbezogene Daten unserer Nutzer grundsätzlich nur, soweit dies zur
            Bereitstellung einer funktionsfähigen Website sowie unserer Inhalte und Leistungen erforderlich
            ist. Die Verarbeitung erfolgt regelmäßig nur nach Einwilligung des Nutzers oder auf Grundlage
            einer gesetzlichen Erlaubnis.
          </>
        ),
      },
      {
        h: "3. Kontaktformular & E-Mail-Kontakt",
        b: () => (
          <>
            Bei der Nutzung unseres Kontaktformulars erheben wir Name, E-Mail, optional Telefon und
            Nachricht. Diese Daten werden ausschließlich zur Bearbeitung deiner Anfrage genutzt. Rechtsgrundlage
            ist Art. 6 Abs. 1 lit. b DSGVO (Vertragsanbahnung). Die Daten werden gelöscht, sobald sie für die
            Zweckerreichung nicht mehr erforderlich sind, spätestens jedoch nach 3 Jahren.
          </>
        ),
      },
      {
        h: "4. Reichweiten-Analyse (Pageview-Tracking)",
        b: () => (
          <>
            Wir verwenden ein eigenes, datenschutzfreundliches Tracking, das pro Besuch ausschließlich
            anonym folgendes speichert: aufgerufene Seite, Domain, Sprachversion und eine anonyme
            Sitzungs-ID (im lokalen Browser-Speicher generiert). <strong>Wir speichern keine IP-Adressen,
            keinen User-Agent, kein Browser-Fingerprinting.</strong> Es werden keine Cookies gesetzt.
            Eine Identifizierung des Nutzers ist nicht möglich. Rechtsgrundlage: Art. 6 Abs. 1 lit. f
            DSGVO (berechtigtes Interesse an der Reichweitenmessung).
          </>
        ),
      },
      {
        h: "5. Web Push Benachrichtigungen",
        b: () => (
          <>
            Sofern du der Zustellung von Push-Benachrichtigungen ausdrücklich zustimmst, speichern wir
            den vom Browser generierten Push-Endpoint sowie zwei kryptographische Schlüssel. Diese Daten
            erlauben es uns, dir Benachrichtigungen zu senden – sie enthalten keine personenbezogenen
            Informationen. Du kannst die Zustimmung jederzeit in den Browser-Einstellungen widerrufen.
            Rechtsgrundlage: Art. 6 Abs. 1 lit. a DSGVO (Einwilligung).
          </>
        ),
      },
      {
        h: "6. SSL-Verschlüsselung",
        b: () => (
          <>
            Diese Seite nutzt aus Gründen der Sicherheit und zum Schutz der Übertragung vertraulicher
            Inhalte eine SSL-Verschlüsselung. Du erkennst sie am Schlüsselsymbol im Browser und am
            HTTPS in der Adresszeile.
          </>
        ),
      },
      {
        h: "7. Hosting",
        b: () => (
          <>
            Das Hosting dieser Website erfolgt über Emergent (USA). Der Anbieter ist über
            Standardvertragsklauseln nach Art. 46 DSGVO verpflichtet. Eine Auftragsverarbeitungs-Vereinbarung
            (AVV) liegt vor.
          </>
        ),
      },
      {
        h: "8. WhatsApp-Verlinkung",
        b: () => (
          <>
            Beim Klick auf einen WhatsApp-Button wirst du an WhatsApp Ireland Limited weitergeleitet.
            Wir haben keinen Einfluss auf die Datenverarbeitung von WhatsApp / Meta. Bitte beachte
            die Datenschutzerklärung von WhatsApp:{" "}
            <a className="text-sky-700 hover:underline" href="https://www.whatsapp.com/legal/privacy-policy-eea" target="_blank" rel="noreferrer">
              www.whatsapp.com/legal/privacy-policy-eea
            </a>
          </>
        ),
      },
      {
        h: "9. E-Mail-Versand & Benachrichtigungen",
        b: () => (
          <>
            Bei eingehenden Leads wird automatisch eine Benachrichtigungs-Mail an unsere Admin-Adresse
            verschickt. Der Versand erfolgt über unseren E-Mail-Anbieter United-Domains. Es findet keine
            Weitergabe von Daten zu Werbezwecken statt.
          </>
        ),
      },
      {
        h: "10. Deine Rechte",
        b: () => (
          <>
            Du hast jederzeit das Recht auf:<br />
            <ul className="list-disc pl-6 mt-2 space-y-1">
              <li>Auskunft (Art. 15 DSGVO)</li>
              <li>Berichtigung (Art. 16 DSGVO)</li>
              <li>Löschung (Art. 17 DSGVO)</li>
              <li>Einschränkung der Verarbeitung (Art. 18 DSGVO)</li>
              <li>Datenübertragbarkeit (Art. 20 DSGVO)</li>
              <li>Widerspruch (Art. 21 DSGVO)</li>
              <li>Widerruf erteilter Einwilligungen (Art. 7 Abs. 3 DSGVO)</li>
              <li>Beschwerde bei einer Aufsichtsbehörde (Art. 77 DSGVO)</li>
            </ul>
            Wende dich für die Ausübung deiner Rechte einfach formlos per E-Mail an uns.
          </>
        ),
      },
      {
        h: "11. Speicherdauer",
        b: () => (
          <>
            Personenbezogene Daten werden gelöscht oder anonymisiert, sobald der Zweck der Speicherung
            entfällt. Lead-Daten: bis zu 3 Jahre nach letztem Kontakt. Pageview-Daten: 12 Monate.
            Push-Subscriptions: bis zum Widerruf der Einwilligung.
          </>
        ),
      },
    ],
  },
  en: {
    title: "Privacy Policy",
    subtitle: "GDPR · LOPD compliant · 2026",
    sections: [
      {
        h: "1. Data Controller",
        b: (c) => (
          <>
            Responsible for the processing of personal data on this website:<br /><br />
            <strong>{c.name}</strong><br />
            {c.address}<br />
            Email: <a className="text-sky-700 hover:underline" href={`mailto:${c.email}`}>{c.email}</a>
          </>
        ),
      },
      {
        h: "2. Contact Form",
        b: () => "When using our contact form we collect name, email, optional phone number and message. The data is used exclusively to process your enquiry (Art. 6 (1) (b) GDPR). Retention: max. 3 years.",
      },
      {
        h: "3. Analytics (Pageview Tracking)",
        b: () => "We use a privacy-friendly tracker that records only: page path, domain, locale and an anonymous session ID. No IP, no User-Agent, no browser fingerprinting. No cookies are set. Legal basis: Art. 6 (1) (f) GDPR (legitimate interest).",
      },
      {
        h: "4. Web Push Notifications",
        b: () => "Only with your explicit consent. We store a push endpoint and two cryptographic keys. You can revoke consent any time in your browser settings.",
      },
      {
        h: "5. Hosting",
        b: () => "This website is hosted by Emergent (USA). Standard Contractual Clauses (Art. 46 GDPR) apply.",
      },
      {
        h: "6. Your Rights",
        b: () => "You have the right to access, rectification, erasure, restriction, portability, objection (Art. 15–21 GDPR) and to lodge a complaint with a supervisory authority (Art. 77 GDPR).",
      },
    ],
  },
  es: {
    title: "Política de Privacidad",
    subtitle: "RGPD · LOPDGDD · 2026",
    sections: [
      {
        h: "1. Responsable del tratamiento",
        b: (c) => (
          <>
            Responsable del tratamiento de datos personales en este sitio web:<br /><br />
            <strong>{c.name}</strong><br />
            {c.address}<br />
            Email: <a className="text-sky-700 hover:underline" href={`mailto:${c.email}`}>{c.email}</a>
          </>
        ),
      },
      {
        h: "2. Formulario de contacto",
        b: () => "Recogemos nombre, email, teléfono opcional y mensaje. Únicamente para procesar su consulta (Art. 6.1.b RGPD). Conservación: máx. 3 años.",
      },
      {
        h: "3. Analítica (medición de páginas vistas)",
        b: () => "Utilizamos un sistema propio respetuoso con la privacidad: solo guardamos la ruta de la página, el dominio, el idioma y un ID de sesión anónimo. No registramos IP, User-Agent ni huella digital. No se utilizan cookies. Base legal: Art. 6.1.f RGPD (interés legítimo).",
      },
      {
        h: "4. Notificaciones Push",
        b: () => "Solo con su consentimiento explícito. Puede revocarlo en cualquier momento desde la configuración del navegador.",
      },
      {
        h: "5. Hospedaje",
        b: () => "Este sitio está alojado por Emergent (USA). Se aplican las Cláusulas Contractuales Tipo (Art. 46 RGPD).",
      },
      {
        h: "6. Sus derechos",
        b: () => "Tiene derecho a acceso, rectificación, supresión, limitación, portabilidad y oposición (Art. 15-21 RGPD) y a presentar una reclamación ante la AEPD.",
      },
    ],
  },
};

const Privacy = () => {
  const { locale } = useLanguage();
  const { content } = useSiteContent();
  const data = CONTENT[locale] || CONTENT.de;
  const controllerCfg = content?.legal?.privacy?.controller || {};
  const c = { ...FALLBACK_CONTROLLER, ...controllerCfg };

  const canonicalUrl = absoluteUrl("/datenschutz");
  const alternates = buildHreflangAlternates("/datenschutz");

  return (
    <div data-testid="page-privacy" className="min-h-screen bg-[#fdfbf7] text-neutral-900">
      <Helmet>
        <html lang={locale} />
        <title>{`${data.title} · ${SITE_NAME}`}</title>
        <meta name="description" content={`${data.title} – ${data.subtitle}`} />
        <link rel="canonical" href={canonicalUrl} />
        {alternates.map((a) => (
          <link key={a.hrefLang} rel="alternate" hrefLang={a.hrefLang} href={a.href} />
        ))}
        <meta name="robots" content="index, follow" />
      </Helmet>
      <Navigation />
      <main className="pt-32 lg:pt-40 pb-24">
        <div className="container-px mx-auto max-w-3xl">
          <div className="text-xs uppercase tracking-[0.22em] text-sky-700 font-medium">
            {data.subtitle}
          </div>
          <h1 className="font-display mt-3 text-4xl lg:text-6xl font-light tracking-tighter">
            {data.title}
          </h1>
          <div className="mt-12 space-y-10">
            {data.sections.map((s, i) => (
              <section key={i} className="space-y-3" data-testid={`privacy-section-${i + 1}`}>
                <h2 className="font-display text-xl lg:text-2xl font-medium">{s.h}</h2>
                <div className="text-neutral-700 leading-relaxed">
                  {typeof s.b === "function" ? s.b(c) : s.b}
                </div>
              </section>
            ))}
          </div>
        </div>
      </main>
      <Footer />
      <WhatsAppButton />
    </div>
  );
};

export default Privacy;
